import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getUser,
  getUserAddresses,
  getUserSerials,
  postNewComment,
  deleteComment,
  deleteUserAddress,
} from "../../../components/users/functions";
import { getProducts } from "../../../components/products/functions";
import { statusObject } from "../../../components/utils/allProductStatus";
import {
  cashnator,
  cpfMask,
  formatarData,
  formatarDataCompleta,
  phoneMask,
} from "../../../components/utils/functions";
import { getGradingTypeLabel, priorityTypes } from "../../../components/utils/gradingTypes";
import ToastContext from "../../../context/toastProvider";
import MobileSection from "../../ui/MobileSection";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileError from "../../ui/MobileError";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileConfirm from "../../ui/MobileConfirm";
import MobileNewAddressSheet from "../products/MobileNewAddressSheet";
import {
  MobileUserEditSheet,
  MobileUserPassSheet,
  MobileUserStoreSheet,
  MobileUserSerialSheet,
} from "./MobileUserDetailSheets";

const ACTIVE_STATUSES = ["APPROVED", "SENT", "QUEUE", "GRADING", "GRADED", "AWAITING"];

const actionBtnClass =
  "inline-flex min-h-[36px] items-center rounded-lg border border-primary/50 px-3 text-xs font-medium text-laranjinha/80 hover:bg-monao disabled:opacity-50";

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex justify-between gap-4 border-b border-primary/20 py-2 text-sm last:border-0">
      <span className="text-laranjinha/60">{label}</span>
      <span className="text-right text-laranjinha">{value}</span>
    </div>
  );
}

export default function MobileUserDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { goodToast, badToast } = useContext(ToastContext);

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [serials, setSerials] = useState([]);
  const [serialsLoading, setSerialsLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [passOpen, setPassOpen] = useState(false);
  const [storeOpen, setStoreOpen] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);

  const [newComment, setNewComment] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [deleteCommentTarget, setDeleteCommentTarget] = useState(null);
  const [deleteAddressTarget, setDeleteAddressTarget] = useState(null);
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [serialSheetOpen, setSerialSheetOpen] = useState(false);

  const isPartner = userData?.roles?.includes("partner");

  const loadUser = useCallback(async () => {
    setLoading(true);
    setError(false);
    const data = await getUser(user?.token, id);
    if (data?.type !== "success") {
      setError(true);
      setLoading(false);
      return;
    }
    setUserData(data.data);
    setLoading(false);
  }, [user?.token, id]);

  const loadAddresses = useCallback(async () => {
    if (!userData?._id) return;
    setAddressesLoading(true);
    const data = await getUserAddresses(user?.token, userData._id);
    if (data?.type === "success") {
      setAddresses(data.data ?? []);
    }
    setAddressesLoading(false);
  }, [user?.token, userData?._id]);

  const loadProducts = useCallback(async () => {
    if (!userData?.email) return;
    setProductsLoading(true);
    const data = await getProducts(user?.token, userData.email);
    if (data?.type === "success") {
      setProducts(
        (data.data ?? []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )
      );
    }
    setProductsLoading(false);
  }, [user?.token, userData?.email]);

  const loadSerials = useCallback(async () => {
    if (!userData?._id) return;
    setSerialsLoading(true);
    const data = await getUserSerials(user?.token, userData._id);
    if (data?.type === "success") {
      setSerials(data.data ?? []);
    }
    setSerialsLoading(false);
  }, [user?.token, userData?._id]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (!userData) return;
    loadAddresses();
    loadProducts();
    loadSerials();
  }, [userData, loadAddresses, loadProducts, loadSerials]);

  const handleAddComment = async () => {
    if (!newComment.trim() || commentBusy) return;
    setCommentBusy(true);
    const data = await postNewComment(user?.token, userData._id, newComment);
    setCommentBusy(false);
    if (data?.type !== "success") {
      badToast("Erro ao enviar comentário");
      return;
    }
    goodToast("Comentário enviado!");
    setNewComment("");
    loadUser();
  };

  const handleDeleteComment = async () => {
    if (!deleteCommentTarget) return;
    setCommentBusy(true);
    const data = await deleteComment(
      user?.token,
      userData._id,
      deleteCommentTarget._id
    );
    setCommentBusy(false);
    setDeleteCommentTarget(null);
    if (data?.type !== "success") {
      badToast("Erro ao excluir comentário");
      return;
    }
    goodToast("Comentário excluído!");
    loadUser();
  };

  const handleDeleteAddress = async () => {
    if (!deleteAddressTarget) return;
    const data = await deleteUserAddress(user?.token, deleteAddressTarget._id);
    setDeleteAddressTarget(null);
    if (data?.type !== "success") {
      badToast("Erro ao excluir endereço");
      return;
    }
    goodToast("Endereço excluído!");
    loadAddresses();
  };

  const navigateToNewProduct = () => {
    navigate("/manafix/products/create", {
      state: { origin: "users-new", user: userData },
    });
  };

  if (loading) {
    return (
      <div aria-label="Carregando usuário" role="status">
        <MobileSkeleton lines={6} />
      </div>
    );
  }

  if (error || !userData) {
    return (
      <MobileError
        message="Erro ao obter dados do usuário."
        onRetry={loadUser}
      />
    );
  }

  const activeProducts = products.filter((product) =>
    ACTIVE_STATUSES.includes(product.status)
  );

  return (
    <div className="pb-8">
      <MobileSection title="Resumo">
        <DetailRow label="Nome" value={userData.name} />
        <DetailRow label="E-mail" value={userData.email} />
        <DetailRow label="Telefone" value={phoneMask(userData.phone)} />
        <DetailRow label="CPF" value={cpfMask(userData.documentNumber)} />
        <DetailRow label="Nascimento" value={formatarData(userData.birth)} />
        <DetailRow
          label="Google"
          value={userData.isGoogle ? "Sim" : "Não"}
        />
        <DetailRow
          label="Verificado"
          value={userData.verified ? "Sim" : "Não"}
        />
        <DetailRow
          label="Admin"
          value={userData.roles?.includes("admin") ? "Sim" : "Não"}
        />
        <DetailRow
          label="Parceiro"
          value={isPartner ? "Sim" : "Não"}
        />
        {userData.store?.name ? (
          <DetailRow label="Loja" value={userData.store.name} />
        ) : null}
      </MobileSection>

      <div className="flex flex-wrap gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-3">
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          className={actionBtnClass}
        >
          Editar
        </button>
        <button
          type="button"
          onClick={() => setPassOpen(true)}
          className={actionBtnClass}
        >
          Senha
        </button>
        <button
          type="button"
          onClick={() => setStoreOpen(true)}
          disabled={!isPartner}
          className={actionBtnClass}
          title={isPartner ? "" : "Usuário não é parceiro"}
        >
          Parceria
        </button>
        <button
          type="button"
          onClick={navigateToNewProduct}
          className={actionBtnClass}
        >
          Novo pedido
        </button>
      </div>

      <MobileSection title="Comentários">
        {userData.comments?.length ? (
          <ul className="mb-4 space-y-2">
            {userData.comments.map((comment) => (
              <li
                key={comment._id ?? `${comment.date}-${comment.text}`}
                className="rounded-lg border border-primary/30 p-3 text-sm"
              >
                <p className="text-laranjinha">{comment.text}</p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-xs text-laranjinha/60">
                    {formatarDataCompleta(comment.date)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setDeleteCommentTarget(comment)}
                    className="text-xs font-medium text-red-600"
                  >
                    Excluir
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <MobileEmpty title="Nenhum comentário" message="" />
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Novo comentário"
            className="min-h-[44px] flex-1 rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha"
          />
          <button
            type="button"
            onClick={handleAddComment}
            disabled={!newComment.trim() || commentBusy}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-green-700 px-3 text-sm font-medium text-white hover:bg-green-800 disabled:opacity-50"
          >
            Enviar
          </button>
        </div>
      </MobileSection>

      <MobileSection title="Endereços">
        {addressesLoading ? (
          <MobileSkeleton lines={2} />
        ) : addresses.length ? (
          <ul className="mb-4 space-y-2">
            {addresses.map((address) => (
              <li
                key={address._id}
                className="rounded-lg border border-primary/30 p-3 text-sm text-laranjinha"
              >
                <p className="font-medium">{address.name}</p>
                <p>
                  {address.street}, {address.houseNumber}
                </p>
                <p>
                  {address.district} — {address.city}/{address.state}
                </p>
                <p>CEP: {address.postalCode}</p>
                <button
                  type="button"
                  onClick={() => setDeleteAddressTarget(address)}
                  className="mt-2 text-xs font-medium text-red-600"
                >
                  Excluir
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <MobileEmpty title="Nenhum endereço" message="" />
        )}
        <button
          type="button"
          onClick={() => setAddressOpen(true)}
          className={actionBtnClass}
        >
          Novo endereço
        </button>
      </MobileSection>

      <MobileSection title="Pedidos ativos">
        {productsLoading ? (
          <MobileSkeleton lines={2} />
        ) : activeProducts.length ? (
          <ul className="space-y-2">
            {activeProducts.map((product) => (
              <li key={product._id}>
                <button
                  type="button"
                  onClick={() => navigate(`/manafix/products/${product._id}`)}
                  className="w-full rounded-lg border border-primary/30 p-3 text-left text-sm hover:bg-monao"
                >
                  <p className="font-medium text-laranjinha">
                    {getGradingTypeLabel(product.gradingType)}
                  </p>
                  <p className="text-laranjinha/70">
                    {statusObject[product.status]} · {cashnator(product.finalPrice)}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <MobileEmpty title="Nenhum pedido ativo" message="" />
        )}
      </MobileSection>

      <MobileSection title="Histórico de pedidos">
        {productsLoading ? (
          <MobileSkeleton lines={2} />
        ) : products.length ? (
          <ul className="space-y-2">
            {products.map((product) => (
              <li key={product._id}>
                <button
                  type="button"
                  onClick={() => navigate(`/manafix/products/${product._id}`)}
                  className="w-full rounded-lg border border-primary/30 p-3 text-left text-sm hover:bg-monao"
                >
                  <p className="font-medium text-laranjinha">
                    {getGradingTypeLabel(product.gradingType)} ·{" "}
                    {priorityTypes[product.priority]}
                  </p>
                  <p className="text-laranjinha/70">
                    {statusObject[product.status]} · {formatarData(product.createdAt)}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <MobileEmpty title="Nenhum pedido" message="" />
        )}
      </MobileSection>

      <MobileSection title="Seriais">
        {serialsLoading ? (
          <MobileSkeleton lines={2} />
        ) : serials.length ? (
          <ul className="space-y-2">
            {serials.map((serial) => (
              <li
                key={serial._id}
                className="rounded-lg border border-primary/30 p-3 text-sm text-laranjinha"
              >
                <p className="font-medium">{serial.serialNumber ?? serial._id}</p>
                <p className="text-laranjinha/70">
                  {serial.gradableModel?.name ?? "—"}
                  {serial.gradableModel?.game ? ` · ${serial.gradableModel.game}` : ""}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSerial(serial);
                    setSerialSheetOpen(true);
                  }}
                  className={`mt-2 ${actionBtnClass}`}
                >
                  Ver
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <MobileEmpty title="Nenhum serial" message="" />
        )}
      </MobileSection>

      <MobileUserEditSheet
        open={editOpen}
        onClose={() => setEditOpen(false)}
        user={user}
        userData={userData}
        onSuccess={loadUser}
      />
      <MobileUserPassSheet
        open={passOpen}
        onClose={() => setPassOpen(false)}
        user={user}
        userData={userData}
      />
      <MobileUserStoreSheet
        open={storeOpen}
        onClose={() => setStoreOpen(false)}
        user={user}
        userData={userData}
        onSuccess={loadUser}
      />
      <MobileNewAddressSheet
        open={addressOpen}
        onClose={() => setAddressOpen(false)}
        user={user}
        userId={userData._id}
        onCreated={() => {
          setAddressOpen(false);
          loadAddresses();
        }}
      />
      <MobileUserSerialSheet
        open={serialSheetOpen}
        onClose={() => {
          setSerialSheetOpen(false);
          setSelectedSerial(null);
        }}
        serial={selectedSerial}
        userData={userData}
      />

      <MobileConfirm
        open={Boolean(deleteCommentTarget)}
        title="Excluir comentário"
        message="Deseja excluir este comentário?"
        onCancel={() => setDeleteCommentTarget(null)}
        onConfirm={handleDeleteComment}
      />
      <MobileConfirm
        open={Boolean(deleteAddressTarget)}
        title="Excluir endereço"
        message="Deseja excluir este endereço?"
        onCancel={() => setDeleteAddressTarget(null)}
        onConfirm={handleDeleteAddress}
      />
    </div>
  );
}
